/**
 * Square grid renderer for the Square Stitcher arcade panel.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
import { ArcadeControlButton } from "../../../../games/index.js";
import {
  currentRotation,
  rotationName,
  squareAligned,
  type SquareStitcherMove,
  type SquareStitcherSquare,
  type SquareStitcherState,
} from "../../algorithms/squareStitcher.js";

export function SquareStitcherGrid({
  state,
  squares,
  stitchBySquareId,
  rotateBySquareId,
  activeSquareId,
  onMove,
}: {
  readonly state: SquareStitcherState;
  readonly squares: readonly SquareStitcherSquare[];
  readonly stitchBySquareId: ReadonlyMap<string, SquareStitcherMove>;
  readonly rotateBySquareId: ReadonlyMap<string, SquareStitcherMove>;
  readonly activeSquareId: string | undefined;
  readonly onMove: (move: SquareStitcherMove) => void;
}): React.JSX.Element {
  const stitched = new Set(state.stitchedSquareIds);
  const sorted = squares
    .slice()
    .sort((left, right) => left.row - right.row || left.col - right.col);

  return (
    <div className="grid gap-3 md:grid-cols-3">
      {sorted.map((square) => (
        <SquareStitcherTile
          key={square.id}
          square={square}
          rotation={currentRotation(state, square.id)}
          aligned={squareAligned(state, square)}
          stitched={stitched.has(square.id)}
          stitchMove={stitchBySquareId.get(square.id)}
          rotateMove={rotateBySquareId.get(square.id)}
          active={activeSquareId === square.id}
          onMove={onMove}
        />
      ))}
    </div>
  );
}

function SquareStitcherTile({
  square,
  rotation,
  aligned,
  stitched,
  stitchMove,
  rotateMove,
  active,
  onMove,
}: {
  readonly square: SquareStitcherSquare;
  readonly rotation: number;
  readonly aligned: boolean;
  readonly stitched: boolean;
  readonly stitchMove: SquareStitcherMove | undefined;
  readonly rotateMove: SquareStitcherMove | undefined;
  readonly active: boolean;
  readonly onMove: (move: SquareStitcherMove) => void;
}): React.JSX.Element {
  const color = stitched
    ? panelTokens.accentGreen
    : aligned
      ? panelTokens.accent
      : panelTokens.accentAmber;

  return (
    <div
      className="min-h-[196px] rounded-2xl border p-3"
      style={{
        background: stitched
          ? `${panelTokens.accentGreen}1e`
          : aligned
            ? `${panelTokens.accent}20`
            : `${panelTokens.accentAmber}16`,
        borderColor: active ? panelTokens.accent : `${color}77`,
        boxShadow: active ? `0 0 26px ${panelTokens.accent}55` : "",
      }}
    >
      <div className="flex items-center justify-between gap-2">
        <PanelSurface.SectionLabel style={{ color }}>
          {square.sdp} · {square.phase}
        </PanelSurface.SectionLabel>
        <span className="font-mono text-[10px] font-black uppercase text-slate-300">
          {stitched ? "stitched" : aligned ? "aligned" : "rotate"}
        </span>
      </div>
      <div className="mt-2 text-sm font-black text-white">{square.label}</div>
      <div className="mt-3 grid grid-cols-[82px_minmax(0,1fr)] gap-3">
        <div
          className="flex h-[82px] items-center justify-center rounded-2xl border bg-black/35"
          style={{ borderColor: `${color}88` }}
        >
          <div
            className="font-mono text-[38px] font-black"
            style={{
              color,
              transform: `rotate(${String(rotation * 90)}deg)`,
              transition: "transform 160ms ease",
            }}
          >
            {stitched ? "✓" : "↑"}
          </div>
        </div>
        <div className="min-w-0 font-mono text-[11px] leading-5 text-slate-300">
          <div>target {rotationName(square.targetRotation)}</div>
          <div>current {rotationName(rotation)}</div>
          <div className="truncate">
            grid [{square.row + 1}, {square.col + 1}]
          </div>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <ArcadeControlButton
          label={`Rotate ${square.label}`}
          accent={panelTokens.accentAmber}
          disabled={rotateMove === undefined || stitched}
          onClick={() => {
            if (rotateMove) onMove(rotateMove);
          }}
        >
          Rotate
        </ArcadeControlButton>
        <ArcadeControlButton
          label={`Stitch ${square.label}`}
          accent={panelTokens.accent}
          disabled={stitchMove === undefined || stitched}
          onClick={() => {
            if (stitchMove) onMove(stitchMove);
          }}
        >
          Stitch
        </ArcadeControlButton>
      </div>
    </div>
  );
}

export function verbForSquareStitcherMove(move: SquareStitcherMove): string {
  return move.type === "stitch" ? "Stitch" : "Rotate";
}
